import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getWorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_service-WorkflowCaseService_connectquery'
import {
  GetWorkflowCaseRequestSchema,
  type GetWorkflowCaseResponse,
} from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * ワークフローケース詳細取得 hook。
 * WorkflowBackBar と WorkflowCaseDetail ページで cache key を共有する
 * (= cache key: ['workflowCase', caseId])。
 *
 * @param options.throwOnError 既定の `ApiClientProvider` は `throwOnError: true`
 *   (= error boundary = 全画面エラーに飛ばす)。 ケース情報が業務データの付帯情報でしかない
 *   画面 (= 取得できなくても本体は描画したい画面) は `false` を渡す。 その場合、 ケース状態が
 *   確定しないため read-only 判定は fail-closed 側 (= 読み取り専用) に倒す責務が呼び出し側にある。
 * @param options.refetchInterval spec AC-WFW-039 の定期自動更新用 (Design Note
 *   `tmp/designing-feature/workflow-auto-update.md` D2)。 既定 (未指定) では
 *   polling しない。 `useCurrentWorkflowCase` 経由で本 hook を共有する 16+ ページ
 *   (read-only 判定用の付帯取得) に polling を波及させないため、 明示的に
 *   `WorkflowCaseDetail` の呼び出しからのみ渡す (直付けしない)。
 */
export function useGetWorkflowCase(
  caseId: string | undefined,
  options?: { throwOnError?: boolean; refetchInterval?: number },
): UseQueryResult<GetWorkflowCaseResponse, ConnectError> {
  return useQuery(
    getWorkflowCase,
    create(GetWorkflowCaseRequestSchema, { id: caseId ?? '' }),
    // throwOnError / refetchInterval は未指定なら key 自体を渡さず QueryClient
    // 既定に従う (= `undefined` を渡すと既定値を上書きしてしまうため)。
    {
      enabled: caseId !== undefined && caseId !== '',
      ...(options?.throwOnError !== undefined
        ? { throwOnError: options.throwOnError }
        : {}),
      ...(options?.refetchInterval !== undefined
        ? { refetchInterval: options.refetchInterval }
        : {}),
    },
  )
}
